import { 
	createSlice,
	createSelector,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'

import {
	envAwarePost,
	dateToAPIStandard
} from '../../../util/util'

export const fetchMerchandiseItems = createAsyncThunk(
	'merchandise/fetchItems',
	async () => await envAwarePost(
		'api/merchandise/',
		{action:'fetchMerchandiseItems'}
	)
)

export const fetchMerchandiseOrders = createAsyncThunk(
	'merchandise/fetchOrders',
	async () => await envAwarePost(
		'api/merchandise/',
		{action:'fetchMerchandiseOrders'}
	)
)

export const createMerchandiseItem = createAsyncThunk(
	'merchandise/createItem',
	async payload => await envAwarePost(
		'api/merchandise/',
		{action:'createMerchandiseItem',...payload}
	)
)

export const editMerchandiseItem = createAsyncThunk(
	'merchandise/editItem',
	async payload => await envAwarePost(
		'api/merchandise/',
		{action:'editMerchandiseItem',...payload}
	)
)

export const deleteMerchandiseItem = createAsyncThunk(
	'merchandise/deleteItem',
	async id => await envAwarePost(
		'api/merchandise/',
		{action:'deleteMerchandiseItem',id}
	)
)

export const editMerchandiseOrderStatus = createAsyncThunk(
	'merchandise/updateOrderStatus',
	async payload => await envAwarePost(
		'api/merchandise/',
		{action:'editMerchandiseOrderStatus',...payload}
	)
)

const itemsAdapter = createEntityAdapter()
const ordersAdapter = createEntityAdapter()

const slice = createSlice({
	name: 'merchandise',
	initialState : {
		items:itemsAdapter.getInitialState({status:'init'}),
		orders:ordersAdapter.getInitialState({status:'init'}),
		status:'init',
		editor:{
			id:false,
			show:false
		},
		creator:{
			show:false
		},
		purchaser:{
			id:false,
			show:false
		},
		//do not collapse this into cart:{<id>:<qty>}
		//we may need to add other meta data like coupons & discounts
		cart:{
			//map from item id to quantity
			itemsQtyMap:{},
			showCheckout:false,
			forms:[]
		}
	},
	reducers : {
		setEditor: (state,action) => {
			state.editor = action.payload
		},
		setCart: (state,action)=>{
			state.cart = {
				...state.cart,
				...action.payload
			}
		},
		setCreator: (state,action) => {
			state.creator = action.payload
		},
		insertNewOrder: (state,action)=>{
			const order = action.payload
			//don't use the adapter so we can force the new
			//order to be at the top
			state.orders.ids.unshift(order.id)
			state.orders.entities[order.id] = order
		},
		setShowCheckout: (state,action)=>{
			state.cart.showCheckout = action.payload
		},
		clearCart: (state) => {
			state.cart.itemsQtyMap = {}
			state.cart.forms = []
		}, 
		updateItemCartQty: (state,action) => {
			const {id,qty} = action.payload
			if(qty === 0){
				delete state.cart.itemsQtyMap[id]
			}else{
				state.cart.itemsQtyMap[id] = qty
			}
		}
	},
	extraReducers : {
		[fetchMerchandiseItems.pending] : state => {
			state.items.status = 'pending'
		},
		[fetchMerchandiseItems.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			state.items.status = 'idle'
			if(success === '1'){
				const {merchandise} = data
				itemsAdapter.upsertMany(state.items,merchandise)
			}
		},
		[createMerchandiseItem.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			if(success === '1') itemsAdapter.upsertOne(state.items,data.merchandise_item)
		},
		[fetchMerchandiseOrders.pending] : state => {
			state.orders.status = 'pending'
		},
		[fetchMerchandiseOrders.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			state.orders.status = 'idle'
			if(success === '1'){
				const {orders} = data
				ordersAdapter.upsertMany(state.orders,orders)
			}
		},
		[editMerchandiseItem.pending] : (state,action)=>{
			const {id,update} = action.meta.arg
			itemsAdapter.updateOne(state.items,{id,changes:update})
		},
		[deleteMerchandiseItem.pending] : (state,action)=>{
			const id = action.meta.arg
			itemsAdapter.removeOne(state.items,id)
		},
		[editMerchandiseOrderStatus.pending] : (state,action)=>{
			const {id,fulfilled,fulfilled_on} = action.meta.arg
			ordersAdapter.updateOne(
				state.orders,
				{ id, changes:{fulfilled,fulfilled_on} }
			)
		}
	}
})

export const {
	setCart,
	clearCart,
	setEditor,
	setCreator,
	setShowCheckout,
	insertNewOrder,
	updateItemCartQty
} = slice.actions

// Export the customized selectors for this itemsAdapter using `getSelectors`
export const {
	selectAll: selectAllMerchandise,
	selectById: selectMerchandiseById,
	selectIds: selectMerchandiseIds
} = itemsAdapter.getSelectors( state => state.merchandise.items )

export const selectItemsByIds = (state,ids)=>(
	ids.map(id=>state.merchandise.items.entities[id])
)

export const {
	selectAll: selectAllOrders,
	selectById: selectOrderById,
	selectIds: selectOrderIds
} = itemsAdapter.getSelectors( state => state.merchandise.orders )


export const selectEditor = state => state.merchandise.editor

export const selectShowCheckout = state => state.merchandise.cart.showCheckout

export const selectCart = state => state.merchandise.cart

export default slice.reducer